import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Check, X, Minus, Swords } from "lucide-react";

/**
 * LandingCompare — head-to-head comparison table (Serpix vs Slack vs Asana).
 * Placed between FAQ and CtaBand. Kills last-mile purchase hesitation.
 */

const COLS = [
  {
    key: "serpix",
    labelKey: "landing.compare.col_serpix",
    highlight: true,
  },
  {
    key: "slack",
    labelKey: "landing.compare.col_slack",
  },
  {
    key: "asana",
    labelKey: "landing.compare.col_asana",
  },
];

// Value tokens: "yes" | "no" | "partial" | string (for price row)
const ROWS = [
  {
    key: "chat_tasks",
    values: { serpix: "yes", slack: "no", asana: "no" },
  },
  {
    key: "views",
    values: { serpix: "yes", slack: "no", asana: "yes" },
  },
  {
    key: "push",
    values: { serpix: "yes", slack: "yes", asana: "partial" },
  },
  {
    key: "onboarding",
    values: { serpix: "yes", slack: "partial", asana: "no" },
  },
  {
    key: "isolation",
    values: { serpix: "yes", slack: "partial", asana: "partial" },
  },
  {
    key: "tr_support",
    values: { serpix: "yes", slack: "no", asana: "no" },
  },
  {
    key: "price",
    // strings, not tokens — will render literal text
    values: {
      serpix: "₺79 / kişi · ay",
      slack: "$8.75 / kişi · ay",
      asana: "$10.99 / kişi · ay",
    },
    isText: true,
  },
];

export function LandingCompare() {
  const { t } = useTranslation();
  const reduced = useReducedMotion();

  return (
    <section
      className="relative overflow-hidden border-t border-zinc-100 bg-gradient-to-b from-zinc-50/50 via-white to-white py-20 md:py-28"
      data-testid="landing-compare"
    >
      {/* Background subtle diagonal lines */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(124,58,237,0.04)_1px,transparent_1px)] bg-[length:24px_24px] opacity-40"
      />

      <div className="relative mx-auto max-w-6xl px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-2xl text-center"
        >
          <div
            className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-700 shadow-sm"
            data-testid="landing-compare-eyebrow"
          >
            <Swords className="h-3 w-3 text-zinc-500" />
            {t("landing.compare.eyebrow")}
          </div>
          <h2
            className="font-display text-3xl font-extrabold leading-tight tracking-tight text-zinc-950 sm:text-4xl md:text-5xl"
            data-testid="landing-compare-title"
          >
            {t("landing.compare.title_prefix")}
            <span className="serpix-gradient-text">
              {t("landing.compare.title_accent")}
            </span>
            {t("landing.compare.title_suffix")}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-zinc-600">
            {t("landing.compare.subtitle")}
          </p>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 overflow-hidden rounded-3xl border border-zinc-200 bg-white shadow-xl shadow-violet-500/5"
        >
          {/* Header row */}
          <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] items-stretch border-b border-zinc-200 bg-zinc-50/60 md:grid-cols-[1.6fr_1fr_1fr_1fr]">
            <div className="hidden px-5 py-4 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 sm:block">
              {t("landing.compare.feature_col")}
            </div>
            <div className="block px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 sm:hidden">
              &nbsp;
            </div>
            {COLS.map((col) => (
              <div
                key={col.key}
                className={
                  "relative flex flex-col items-center justify-center px-3 py-4 text-center " +
                  (col.highlight
                    ? "bg-gradient-to-b from-violet-500/10 to-pink-500/5"
                    : "")
                }
                data-testid={`landing-compare-col-${col.key}`}
              >
                {col.highlight && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-500 to-pink-500"
                  />
                )}
                <div
                  className={
                    "text-sm font-bold tracking-tight sm:text-base " +
                    (col.highlight ? "text-zinc-950" : "text-zinc-500")
                  }
                >
                  {t(col.labelKey)}
                </div>
                {col.highlight && (
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-violet-600">
                    {t("landing.compare.you_pick")}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Body rows */}
          {ROWS.map((row, rIdx) => (
            <div
              key={row.key}
              className={
                "grid grid-cols-[1.4fr_1fr_1fr_1fr] items-stretch border-b border-zinc-100 last:border-b-0 md:grid-cols-[1.6fr_1fr_1fr_1fr] " +
                (rIdx % 2 === 1 ? "bg-zinc-50/40" : "bg-white")
              }
              data-testid={`landing-compare-row-${row.key}`}
            >
              {/* Feature label */}
              <div className="px-4 py-4 text-[13px] font-medium text-zinc-800 sm:px-5 sm:text-sm">
                {t(`landing.compare.rows.${row.key}`)}
              </div>
              {/* Value cells */}
              {COLS.map((col) => {
                const value = row.values[col.key];
                return (
                  <div
                    key={col.key}
                    className={
                      "flex items-center justify-center px-2 py-4 text-center " +
                      (col.highlight
                        ? "bg-gradient-to-b from-violet-500/[0.04] to-pink-500/[0.02]"
                        : "")
                    }
                    data-testid={`landing-compare-cell-${row.key}-${col.key}`}
                  >
                    {row.isText ? (
                      <div
                        className={
                          "text-[12px] font-semibold sm:text-[13px] " +
                          (col.highlight ? "text-violet-700" : "text-zinc-700")
                        }
                      >
                        {value}
                      </div>
                    ) : (
                      <ValueMark value={value} highlight={col.highlight} />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </motion.div>

        {/* Footnote */}
        <motion.p
          initial={reduced ? false : { opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-5 text-center text-[11px] text-zinc-400"
        >
          {t("landing.compare.disclaimer")}
        </motion.p>
      </div>
    </section>
  );
}

function ValueMark({ value, highlight }) {
  if (value === "yes") {
    return (
      <span
        className={
          "flex h-6 w-6 items-center justify-center rounded-full sm:h-7 sm:w-7 " +
          (highlight
            ? "bg-gradient-to-br from-violet-500 to-pink-500 text-white shadow-md shadow-violet-500/40"
            : "bg-emerald-500 text-white shadow-sm")
        }
        aria-label="Yes"
      >
        <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={3} />
      </span>
    );
  }
  if (value === "partial") {
    return (
      <span
        className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-700 sm:h-7 sm:w-7"
        aria-label="Partial"
      >
        <Minus className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={3} />
      </span>
    );
  }
  return (
    <span
      className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-100 text-zinc-400 sm:h-7 sm:w-7"
      aria-label="No"
    >
      <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={3} />
    </span>
  );
}
